import React, { useEffect } from "react";
import useUserStore from "../../store/userStore";
import { shallow } from "zustand/shallow";
import { useTracker } from "../../wrappers/tracker";
import useProfile from "./useProfile";
import { useFirebase } from "../../wrappers/firebase";
import { useQueryClient } from "@tanstack/react-query";
import { increment, serverTimestamp } from "firebase/firestore";
/**
 * @param {Object} props
 * @param {'post' | 'product' | 'comment'} props.type
 * @param {string} props.contentUserId The userID of the publisher of the content
 * @param {string} props.contentId The uid of the content itself
 * @returns {{
 * addLikeReaction: () => {},
 * addDislikeReaction: () => {},
 * removeDislikeReaction: () => {},
 * removeLikeReaction: () => {},
 * }}
 */
export default function useReactions({ type, contentUserId, contentId }) {
  const { id: stateUserId } = useUserStore((state) => state.user, shallow);
  const { updateDocument, addDocument } = useFirebase();
  const queryClient = useQueryClient();

  function addLikeReaction() {
    const { isDislikedByUser } = removeDislikeReaction();
    let _likeCount = 0;
    queryClient.setQueryData([`${type}s`, contentId], (oldData) => {
      _likeCount = oldData.likeCount + 1;
      return {
        ...oldData,
        likeCount: oldData.likeCount + 1,
        isLikedByUser: true,
      };
    });

    // Fire-and-forget background work
    (async () => {
      try {
        await updateDocument({
          path: `profiles/${contentUserId}/${type}s/${contentId}/likes/${stateUserId}`,
          data: {
            createdAt: serverTimestamp(),
            isDeleted: false,
          },
        });
        await updateDocument({
          path: `profiles/${contentUserId}/${type}s/${contentId}`,
          data: {
            likeCount: increment(1),
          },
        });
      } catch (err) {
        console.error("Failed to create like:", err);
        // optionally: rollback queryClient.setQueryData here
      }
    })();
    return { likeCount: _likeCount, isLikedByUser: true, isDislikedByUser };
  }

  function removeLikeReaction() {
    let _likeCount = 0;
    queryClient.setQueryData([`${type}s`, contentId], (oldData) => {
      _likeCount = (oldData.likeCount || 1) - 1;
      return {
        ...oldData,
        likeCount: (oldData.likeCount || 1) - 1,
        isLikedByUser: false,
      };
    });

    // Fire-and-forget background work
    (async () => {
      try {
        await updateDocument({
          path: `profiles/${contentUserId}/${type}s/${contentId}/likes/${stateUserId}`,
          data: {
            createdAt: serverTimestamp(),
            isDeleted: true,
          },
        });
        await updateDocument({
          path: `profiles/${contentUserId}/${type}s/${contentId}`,
          data: {
            likeCount: increment(-1),
          },
        });
      } catch (err) {
        console.error("Failed to persist like:", err);
        // optionally: rollback queryClient.setQueryData here
      }
    })();
    return { likeCount: _likeCount, isLikedByUser: false };
  }

  function addDislikeReaction() {
    console.log("Adding dislike");
    let _likeCount = 0;
    const _removedLike = removeLikeReaction();
    queryClient.setQueryData([`${type}s`, contentId], (oldData) => {
      _likeCount = (oldData.likeCount || 1) - 1;
      return {
        ...oldData,
        isDislikedByUser: true,
        isLikedByUser: false,
      };
    });

    (async () => {
      try {
        await updateDocument({
          path: `profiles/${contentUserId}/${type}s/${contentId}/dislikes/${stateUserId}`,
          data: {
            createdAt: serverTimestamp(),
            isDeleted: false,
          },
        });
      } catch (err) {
        console.error("Failed to update dislike:", err);
        // optionally: rollback queryClient.setQueryData here
      }
    })();

    return { ..._removedLike, likeCount: _likeCount, isDislikedByUser: true };
  }

  function removeDislikeReaction() {
    queryClient.setQueryData([`${type}s`, contentId], (oldData) => {
      return {
        ...oldData,
        isDislikedByUser: false,
      };
    });

    // Fire-and-forget background work
    (async () => {
      try {
        await updateDocument({
          path: `profiles/${contentUserId}/${type}s/${contentId}/dislikes/${stateUserId}`,
          data: {
            createdAt: serverTimestamp(),
            isDeleted: true,
          },
        });
      } catch (err) {
        console.error("Failed to persist like:", err);
        // optionally: rollback queryClient.setQueryData here
      }
    })();
    return { isDislikedByUser: false };
  }

  return {
    addDislikeReaction,
    addLikeReaction,
    removeDislikeReaction,
    removeLikeReaction,
  };
}
